/**
 * Pure, framework-free math for the Options Spread Calculator.
 *
 * Every function here is a plain transformation with no side effects, so it
 * can be unit-tested in isolation (see the verification script referenced in
 * the PR description — there is no test runner wired into this project yet,
 * so ad-hoc verification is run via `node --experimental-strip-types`).
 *
 * Conventions:
 *   - `premium` is always PER SHARE (e.g. 4.50, not 450).
 *   - `strike` is the option's strike price.
 *   - `contracts` is the number of option contracts (each = 100 shares).
 *   - All money outputs (payoff, net premium, max profit/loss) are TOTAL
 *     dollars — i.e. already multiplied by 100 * contracts.
 *   - Every leg carries its OWN `expiryDays` and `iv` (per-leg, not shared
 *     strategy-wide values). `evalDay` is days-from-today; a leg's remaining
 *     time is `max(leg.expiryDays - evalDay, 0)`, so mixed-expiry positions
 *     (e.g. calendars) value correctly: the near leg goes to intrinsic while
 *     the far leg still carries time value.
 *   - `ivShift` (decimal, e.g. 0.10 for +10 vol points) is applied to every
 *     leg's `iv` before pricing, to simulate a vol crush/spike scenario.
 */

export type OptionAction = 'buy' | 'sell'
export type OptionType = 'call' | 'put'

export interface OptionLeg {
  id: string
  action: OptionAction
  type: OptionType
  strike: number
  /** Premium per share (not multiplied by 100 or contracts). */
  premium: number
  contracts: number
  /**
   * Days from today until THIS leg expires. Each leg carries its own expiry
   * so calendar-style positions (different expiries per leg) value
   * correctly — see `earliestExpiry` and the per-leg `daysRemaining`
   * computation used throughout the pricing functions below.
   */
  expiryDays: number
  /**
   * Per-leg implied volatility (decimal, e.g. 0.324 for 32.4%) — always a
   * concrete value. The UI seeds new legs from the strategy-wide default IV
   * and keeps non-overridden legs in sync with it; `solveIvFromDelta` is the
   * usual way this gets overwritten with a chain-implied value (broker-quoted
   * delta -> back-solved IV). `ivShift` is applied on top of this at
   * evaluation time, not baked in here.
   */
  iv: number
  /**
   * Broker-quoted market delta the user typed for this leg (decimal, calls
   * in (0, 1), puts in (-1, 0)). This is the raw input that `iv` above was
   * solved from; kept separately so the UI can re-solve `iv` whenever S,
   * expiry, rate, strike, or type change without losing the user's original
   * delta entry. Undefined = leg has no market-delta override.
   */
  marketDelta?: number
}

/** A single long/short shares position, tracked alongside the option legs. */
export interface StockPosition {
  direction: 'long' | 'short'
  shares: number
  /** Cost basis per share (what the shares were bought/shorted at). */
  entryPrice: number
}

export interface PayoffPoint {
  price: number
  profit: number
}

export type SpreadTemplate =
  | 'bull_call_spread'
  | 'bear_call_spread'
  | 'bull_put_spread'
  | 'bear_put_spread'
  | 'custom'

export interface SpreadSummary {
  /** Options-only net premium. Positive = net debit; negative = net credit. */
  netPremium: number
  /**
   * Total cost to open the position, options + stock. Positive = net debit
   * (cost to open); negative = net credit (received).
   */
  entryCost: number
  maxProfit: number | 'Unlimited'
  maxLoss: number | 'Unlimited'
  breakevens: number[]
  /** maxProfit / |maxLoss|, or null when either side is unbounded/zero. */
  returnOnRisk: number | null
}

// ─── Per-leg payoff ─────────────────────────────────────────────────────────

/** Intrinsic value per share at expiry for a single leg, given underlying price S. */
function intrinsicValue(leg: OptionLeg, price: number): number {
  return leg.type === 'call' ? Math.max(price - leg.strike, 0) : Math.max(leg.strike - price, 0)
}

/** Total dollar profit/loss for one leg at expiry, given underlying price S. */
export function legPayoff(leg: OptionLeg, price: number): number {
  const intrinsic = intrinsicValue(leg, price)
  const perShare = leg.action === 'buy' ? intrinsic - leg.premium : leg.premium - intrinsic
  return perShare * 100 * leg.contracts
}

/** Total dollar profit/loss for a stock position at price S (shares held to the price shown, no expiry). */
export function stockPnl(pos: StockPosition, price: number): number {
  const sign = pos.direction === 'long' ? 1 : -1
  return sign * pos.shares * (price - pos.entryPrice)
}

/**
 * Total dollar profit/loss across all legs (each valued at ITS OWN expiry,
 * i.e. pure intrinsic value regardless of the other legs' expiries) plus an
 * optional stock position, at underlying price S. This is the "at expiry"
 * view — for mixed-expiry positions it represents "if price sat at S until
 * every leg's own expiry", which is exact for same-expiry spreads and a
 * reasonable idealization for calendars.
 */
export function totalPayoff(legs: OptionLeg[], price: number, stockPosition?: StockPosition): number {
  const legsTotal = legs.reduce((sum, leg) => sum + legPayoff(leg, price), 0)
  return legsTotal + (stockPosition ? stockPnl(stockPosition, price) : 0)
}

/** Earliest expiry (days from today) across all legs — the last day evaluation is fully meaningful. */
export function earliestExpiry(legs: OptionLeg[]): number {
  if (legs.length === 0) return 0
  return Math.min(...legs.map((l) => l.expiryDays))
}

// ─── Payoff curve for charting ──────────────────────────────────────────────

export interface PriceRange {
  min: number
  max: number
  steps?: number
}

/**
 * Default charting range: lowest strike (and stock entry price, if any) -20%
 * to the highest +20%. Falls back to a small window around 100 if there are
 * no legs/stock position yet.
 */
export function defaultPriceRange(
  legs: OptionLeg[],
  stockPosition?: StockPosition
): { min: number; max: number } {
  const anchors = legs.map((l) => l.strike)
  if (stockPosition) anchors.push(stockPosition.entryPrice)
  if (anchors.length === 0) return { min: 80, max: 120 }
  const lo = Math.min(...anchors)
  const hi = Math.max(...anchors)
  return {
    min: Math.max(0, lo * 0.8),
    max: hi * 1.2,
  }
}

/** Generates {price, profit} points across the given range for the payoff-at-expiry chart. */
export function payoffAtExpiry(
  legs: OptionLeg[],
  range: PriceRange,
  stockPosition?: StockPosition
): PayoffPoint[] {
  const { min, max } = range
  const steps = range.steps ?? 120
  if ((legs.length === 0 && !stockPosition) || steps <= 0 || max <= min) return []

  const points: PayoffPoint[] = []
  for (let i = 0; i <= steps; i++) {
    const price = min + ((max - min) * i) / steps
    points.push({ price, profit: totalPayoff(legs, price, stockPosition) })
  }
  return points
}

/** Rounds a raw step size up to a "nice" 1/2/5 * 10^n value, for evenly-spaced grid labels. */
function niceStep(rawStep: number): number {
  if (!(rawStep > 0)) return 1
  const exponent = Math.floor(Math.log10(rawStep))
  const magnitude = Math.pow(10, exponent)
  const fraction = rawStep / magnitude
  let niceFraction: number
  if (fraction <= 1) niceFraction = 1
  else if (fraction <= 2) niceFraction = 2
  else if (fraction <= 5) niceFraction = 5
  else niceFraction = 10
  return niceFraction * magnitude
}

/** Rounds a raw step size DOWN to a "nice" 1/2/5 * 10^n value (never larger than rawStep). */
function niceStepDown(rawStep: number): number {
  if (!(rawStep > 0)) return 1
  const exponent = Math.floor(Math.log10(rawStep))
  const magnitude = Math.pow(10, exponent)
  const fraction = rawStep / magnitude
  let niceFraction: number
  if (fraction >= 5) niceFraction = 5
  else if (fraction >= 2) niceFraction = 2
  else niceFraction = 1
  return niceFraction * magnitude
}

/**
 * Builds a round-number underlying-price grid for the P&L table, centred on
 * `centerPrice` (typically the current underlying price) and spanning
 * roughly `range`, with `targetRows` rows (~15-21 recommended). Step size is
 * rounded to a "nice" 1/2/5 * 10^n increment so row prices read cleanly
 * (e.g. $95, $100, $105 rather than $94.62, $99.87, ...). Prices below 0 are
 * dropped, since the underlying can't go negative.
 */
export function buildPriceGrid(range: PriceRange, centerPrice: number, targetRows = 17): number[] {
  if (targetRows <= 0) return []
  const span = Math.max(range.max - range.min, 1)
  const rawStep = span / Math.max(targetRows - 1, 1)
  // Round DOWN to the nearest nice step so the grid never spans wider than
  // the requested range (rounding up can overshoot the range by >50%).
  let step = niceStep(rawStep)
  if (step > rawStep) step = niceStepDown(rawStep)
  const center = Math.round(centerPrice / step) * step
  const half = Math.floor(targetRows / 2)

  const prices: number[] = []
  for (let i = 0; i < targetRows; i++) {
    const price = center + (i - half) * step
    if (price >= 0) prices.push(Math.round(price * 100) / 100)
  }
  return prices
}

export interface PriceGridParams {
  min: number
  max: number
  step: number
}

/**
 * Derives {min, max, step} from the "nice"-stepped auto grid (`buildPriceGrid`)
 * — used to prefill the matrix's editable Min/Max/Step controls and to
 * restore them when the user hits "Auto".
 */
export function autoPriceGridParams(range: PriceRange, centerPrice: number, targetRows = 17): PriceGridParams {
  const grid = buildPriceGrid(range, centerPrice, targetRows)
  if (grid.length === 0) return { min: range.min, max: range.max, step: 1 }
  const min = grid[0]
  const max = grid[grid.length - 1]
  const step = grid.length > 1 ? Math.round((grid[1] - grid[0]) * 100) / 100 : Math.max(1, max - min)
  return { min, max, step: step > 0 ? step : 1 }
}

/**
 * Builds a price grid directly from explicit min/max/step (the matrix's
 * editable range controls), rather than the "nice number" auto grid. Clamps
 * to at most `maxRows` rows and returns an empty array for invalid ranges
 * (max <= min or step <= 0) so the caller can fall back to the auto grid.
 */
/**
 * Price grid spanning centerPrice ± rangePct with "nice" round steps.
 * Unlike buildPriceGrid (which fixes the row count and may not fill the
 * range), this always covers the full requested range: it prefers the finer
 * nice step when that fits within maxRows, otherwise the coarser one. Rows
 * land on clean multiples of the step (e.g. 71.4, 71.6, ... for a 20¢ step).
 */
export function buildCenteredPriceGrid(
  centerPrice: number,
  rangePct: number,
  targetRows = 17,
  maxRows = 25
): number[] {
  if (!(centerPrice > 0) || !(rangePct > 0) || targetRows <= 1) return []
  const half = centerPrice * rangePct
  const raw = (2 * half) / (targetRows - 1)
  const fine = niceStepDown(raw)
  const coarse = niceStep(raw)
  const step = (2 * half) / fine + 1 <= maxRows ? fine : coarse
  const min = Math.ceil((centerPrice - half) / step) * step
  const max = centerPrice + half
  return buildPriceGridFromRange(min, max, step, maxRows + 1)
}

export function buildPriceGridFromRange(min: number, max: number, step: number, maxRows = 60): number[] {
  if (!(step > 0) || !(max > min)) return []
  const prices: number[] = []
  for (let price = min; price <= max + 1e-9 && prices.length < maxRows; price += step) {
    if (price >= 0) prices.push(Math.round(price * 100) / 100)
  }
  return prices
}

// ─── Black-Scholes pricing & Greeks ─────────────────────────────────────────
//
// Continuous-compounding Black-Scholes with an optional continuous dividend
// yield `q` (defaults to 0 — no dividends for v1). All rate/vol inputs are
// DECIMAL fractions (e.g. 0.20 for 20% IV, 0.05 for 5% risk-free), not
// percentages — conversion from a percent input field happens at the UI
// layer. `tau` is time-to-expiry in YEARS (daysRemaining / 365).
//
// Every function here guards against tau <= 0 / sigma <= 0 (falls back to
// the expiry payoff, i.e. intrinsic value and step-function Greeks) and
// against NaN/Infinity results, so the UI can call these on every render
// without needing its own edge-case handling.

/**
 * Abramowitz & Stegun 7.1.26 rational approximation of the error function.
 * Max absolute error ~1.5e-7 — more than accurate enough for option pricing
 * display (we verify against canonical values to a 0.001 tolerance).
 */
function erf(x: number): number {
  const sign = x < 0 ? -1 : 1
  const ax = Math.abs(x)

  const a1 = 0.254829592
  const a2 = -0.284496736
  const a3 = 1.421413741
  const a4 = -1.453152027
  const a5 = 1.061405429
  const p = 0.3275911

  const t = 1 / (1 + p * ax)
  const y = 1 - (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t) * Math.exp(-ax * ax)
  return sign * y
}

/** Standard normal CDF, N(x). */
export function normCdf(x: number): number {
  if (!isFinite(x)) return x > 0 ? 1 : 0
  return 0.5 * (1 + erf(x / Math.SQRT2))
}

/** Standard normal PDF, φ(x). */
export function normPdf(x: number): number {
  if (!isFinite(x)) return 0
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI)
}

/** d1 term of Black-Scholes. Returns NaN if inputs make the formula undefined (S/K <= 0, tau <= 0, sigma <= 0). */
export function bsD1(S: number, K: number, tau: number, sigma: number, r: number, q = 0): number {
  if (!(S > 0) || !(K > 0) || !(tau > 0) || !(sigma > 0)) return NaN
  const d1 = (Math.log(S / K) + (r - q + (sigma * sigma) / 2) * tau) / (sigma * Math.sqrt(tau))
  return isFinite(d1) ? d1 : NaN
}

/** d2 = d1 - sigma * sqrt(tau). Returns NaN under the same conditions as bsD1. */
export function bsD2(S: number, K: number, tau: number, sigma: number, r: number, q = 0): number {
  const d1 = bsD1(S, K, tau, sigma, r, q)
  if (!isFinite(d1)) return NaN
  return d1 - sigma * Math.sqrt(tau)
}

/**
 * Theoretical Black-Scholes price for a single call or put.
 * Falls back to intrinsic value when tau <= 0, sigma <= 0, or either price
 * input is non-positive (i.e. at/after expiry, or a degenerate input).
 */
export function bsPrice(
  type: OptionType,
  S: number,
  K: number,
  tau: number,
  sigma: number,
  r: number,
  q = 0
): number {
  const intrinsic = type === 'call' ? Math.max(S - K, 0) : Math.max(K - S, 0)
  if (!(S > 0) || !(K > 0) || !(tau > 0) || !(sigma > 0)) return intrinsic

  const d1 = bsD1(S, K, tau, sigma, r, q)
  if (!isFinite(d1)) return intrinsic
  const d2 = d1 - sigma * Math.sqrt(tau)

  const discountedSpot = S * Math.exp(-q * tau)
  const discountedStrike = K * Math.exp(-r * tau)

  const price =
    type === 'call'
      ? discountedSpot * normCdf(d1) - discountedStrike * normCdf(d2)
      : discountedStrike * normCdf(-d2) - discountedSpot * normCdf(-d1)

  return isFinite(price) ? price : intrinsic
}

/**
 * Implied volatility from an observed option premium — inverts bsPrice via
 * bisection (price is strictly increasing in sigma, so the root is unique).
 * Returns null when the premium sits outside the no-arbitrage bounds for a
 * European option (below discounted intrinsic, or above S / discounted K),
 * in which case callers should fall back to a user-supplied IV.
 *
 * This is what keeps T+0 P&L at ~$0 for freshly entered legs: pricing each
 * leg with the IV implied by its own entry premium means "theoretical value
 * minus premium paid" starts at zero at the current spot, exactly like
 * professional calculators.
 */
export function impliedVolFromPrice(
  type: OptionType,
  S: number,
  K: number,
  tau: number,
  r: number,
  marketPrice: number,
  q = 0
): number | null {
  if (!(S > 0) || !(K > 0) || !(tau > 0) || !isFinite(marketPrice)) return null

  const lowerBound =
    type === 'call'
      ? Math.max(S * Math.exp(-q * tau) - K * Math.exp(-r * tau), 0)
      : Math.max(K * Math.exp(-r * tau) - S * Math.exp(-q * tau), 0)
  const upperBound = type === 'call' ? S * Math.exp(-q * tau) : K * Math.exp(-r * tau)
  if (marketPrice <= lowerBound + 1e-9 || marketPrice >= upperBound - 1e-9) return null

  let lo = 0.005
  let hi = 5.0
  if (bsPrice(type, S, K, tau, lo, r, q) > marketPrice) return null
  if (bsPrice(type, S, K, tau, hi, r, q) < marketPrice) return null

  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2
    if (bsPrice(type, S, K, tau, mid, r, q) < marketPrice) lo = mid
    else hi = mid
  }
  const sigma = (lo + hi) / 2
  return isFinite(sigma) ? sigma : null
}

/**
 * Delta for a single call or put — d(price)/d(S).
 * At/after expiry (tau <= 0 or sigma <= 0) this is the step function of the
 * payoff's slope: call delta is 0/1 (0.5 exactly at the strike), put delta
 * is -1/0 (-0.5 exactly at the strike).
 */
export function bsDelta(
  type: OptionType,
  S: number,
  K: number,
  tau: number,
  sigma: number,
  r: number,
  q = 0
): number {
  if (!(tau > 0) || !(sigma > 0)) {
    if (type === 'call') {
      if (S > K) return 1
      if (S < K) return 0
      return 0.5
    }
    if (S < K) return -1
    if (S > K) return 0
    return -0.5
  }

  const d1 = bsD1(S, K, tau, sigma, r, q)
  if (!isFinite(d1)) return 0

  const discountedQ = Math.exp(-q * tau)
  const delta = type === 'call' ? discountedQ * normCdf(d1) : discountedQ * (normCdf(d1) - 1)
  return isFinite(delta) ? delta : 0
}

/**
 * Gamma — d(delta)/d(S) — identical for calls and puts, so it takes no
 * `type` parameter. Zero at/after expiry (the payoff is piecewise-linear
 * there, so its second derivative is 0 everywhere except an undefined kink).
 */
export function bsGamma(S: number, K: number, tau: number, sigma: number, r: number, q = 0): number {
  if (!(S > 0) || !(tau > 0) || !(sigma > 0)) return 0

  const d1 = bsD1(S, K, tau, sigma, r, q)
  if (!isFinite(d1)) return 0

  const gamma = (Math.exp(-q * tau) * normPdf(d1)) / (S * sigma * Math.sqrt(tau))
  return isFinite(gamma) ? gamma : 0
}

/**
 * Theta — d(price)/d(t), i.e. the rate of change of the option's theoretical
 * value as time passes — for a single call or put. Returned as a PER-YEAR
 * rate (negative for typical long options, since they lose value as tau
 * shrinks); callers wanting per-day decay divide by 365 (see
 * `positionTheta`, which does this after summing/scaling per leg).
 * Zero at/after expiry (tau <= 0) or when sigma <= 0, matching the other
 * Greeks' edge-case handling above.
 */
export function bsTheta(
  type: OptionType,
  S: number,
  K: number,
  tau: number,
  sigma: number,
  r: number,
  q = 0
): number {
  if (!(S > 0) || !(K > 0) || !(tau > 0) || !(sigma > 0)) return 0

  const d1 = bsD1(S, K, tau, sigma, r, q)
  if (!isFinite(d1)) return 0
  const d2 = d1 - sigma * Math.sqrt(tau)

  const discountedSpot = S * Math.exp(-q * tau)
  const discountedStrike = K * Math.exp(-r * tau)
  const term1 = -(discountedSpot * normPdf(d1) * sigma) / (2 * Math.sqrt(tau))

  const theta =
    type === 'call'
      ? term1 - r * discountedStrike * normCdf(d2) + q * discountedSpot * normCdf(d1)
      : term1 + r * discountedStrike * normCdf(-d2) - q * discountedSpot * normCdf(-d1)

  return isFinite(theta) ? theta : 0
}

// ─── Delta → IV solver ──────────────────────────────────────────────────────
//
// Broker option chains quote delta, not IV. `solveIvFromDelta` inverts
// `bsDelta` for a single leg: given a target delta, find the sigma that
// reproduces it. This lets a leg's IV track the real (skewed) chain instead
// of forcing every leg onto one strategy-wide IV.

/**
 * Back-solves the implied volatility that reproduces `targetDelta` for a
 * single call or put, at the given spot/strike/time/rate.
 *
 * Delta is not always monotonic in sigma for ITM options (it can dip and
 * come back up as vol increases), so a target delta can have zero, one, or
 * two solutions. This scans sigma in [0.01, 3.0] at a 0.005 step, brackets
 * every sign change of `bsDelta(sigma) - target`, refines each bracket by
 * bisection, and — if more than one root is found — returns whichever root
 * is nearest `referenceIv` (typically the strategy-wide IV), since that's
 * the economically plausible one for a real chain.
 */
export function solveIvFromDelta(
  type: OptionType,
  S: number,
  K: number,
  tauYears: number,
  r: number,
  targetDelta: number,
  referenceIv: number,
  q = 0
): { iv: number } | { error: string } {
  if (!(tauYears > 0)) {
    return { error: 'Delta is a step function at/after expiry — cannot solve for IV.' }
  }
  if (!(S > 0) || !(K > 0)) {
    return { error: 'Delta unreachable for this strike/DTE.' }
  }

  // Normalize sign: calls live in (0, 1), puts in (-1, 0). Tolerate a
  // caller passing the "unsigned" magnitude for a put.
  const target = type === 'call' ? Math.abs(targetDelta) : -Math.abs(targetDelta)
  if (!isFinite(target) || target === 0 || Math.abs(target) >= 1) {
    return { error: 'Delta must be strictly between -1 and 1, and non-zero.' }
  }

  const SIGMA_MIN = 0.01
  const SIGMA_MAX = 3.0
  const STEP = 0.005
  const BISECTION_ITERS = 40

  const f = (sigma: number): number => bsDelta(type, S, K, tauYears, sigma, r, q) - target

  const roots: number[] = []
  let prevSigma = SIGMA_MIN
  let prevF = f(prevSigma)
  if (prevF === 0) roots.push(prevSigma)

  for (let sigma = SIGMA_MIN + STEP; sigma <= SIGMA_MAX + 1e-9; sigma += STEP) {
    const curF = f(sigma)
    if (curF === 0) {
      roots.push(sigma)
    } else if ((prevF < 0) !== (curF < 0)) {
      let lo = prevSigma
      let hi = sigma
      let fLo = prevF
      for (let iter = 0; iter < BISECTION_ITERS; iter++) {
        const mid = (lo + hi) / 2
        const fMid = f(mid)
        if (fMid === 0) {
          lo = mid
          hi = mid
          break
        }
        if (fLo < 0 === fMid < 0) {
          lo = mid
          fLo = fMid
        } else {
          hi = mid
        }
      }
      roots.push((lo + hi) / 2)
    }
    prevSigma = sigma
    prevF = curF
  }

  if (roots.length === 0) {
    return { error: 'Delta unreachable for this strike/DTE.' }
  }

  let best = roots[0]
  let bestDist = Math.abs(roots[0] - referenceIv)
  for (const root of roots) {
    const dist = Math.abs(root - referenceIv)
    if (dist < bestDist) {
      bestDist = dist
      best = root
    }
  }
  return { iv: best }
}

// ─── Per-leg / per-position evaluation helpers ─────────────────────────────

/** +1 for a long (buy) leg, -1 for a short (sell) leg. */
function legSign(action: OptionAction): 1 | -1 {
  return action === 'buy' ? 1 : -1
}

/** +1 for a long stock position, -1 for a short stock position. */
function stockSign(pos: StockPosition): 1 | -1 {
  return pos.direction === 'long' ? 1 : -1
}

/** Days remaining for a leg at a given evaluation day (days from today). Floors at 0 (intrinsic at/after this leg's own expiry). */
function legDaysRemaining(leg: OptionLeg, evalDay: number): number {
  return Math.max(leg.expiryDays - evalDay, 0)
}

/** Effective volatility for a leg once the scenario-wide `ivShift` is applied. Floored at 0.005 so Black-Scholes never sees sigma <= 0 from a large negative shift. */
export function effectiveSigma(iv: number, ivShift = 0): number {
  return Math.max(iv + ivShift, 0.005)
}

/**
 * Mark-to-market P&L for the whole position — option legs (each priced with
 * its own expiry/IV, shifted by `ivShift`) plus an optional stock position —
 * at underlying price `S` and evaluation day `evalDay` (days from today).
 */
export function plAtTime(
  legs: OptionLeg[],
  S: number,
  evalDay: number,
  r: number,
  ivShift = 0,
  q = 0,
  stockPosition?: StockPosition
): number {
  const legsPl = legs.reduce((sum, leg) => {
    const tau = legDaysRemaining(leg, evalDay) / 365
    const sigma = effectiveSigma(leg.iv, ivShift)
    const theoreticalPrice = bsPrice(leg.type, S, leg.strike, tau, sigma, r, q)
    const perShare = legSign(leg.action) * (theoreticalPrice - leg.premium)
    return sum + perShare * 100 * leg.contracts
  }, 0)
  return legsPl + (stockPosition ? stockPnl(stockPosition, S) : 0)
}

/**
 * Total mark-to-market market VALUE of the position (not P&L) — the sum of
 * each leg's signed theoretical price plus the stock position's market
 * value. Used for the matrix's "Value" output mode.
 */
export function positionValue(
  legs: OptionLeg[],
  S: number,
  evalDay: number,
  r: number,
  ivShift = 0,
  q = 0,
  stockPosition?: StockPosition
): number {
  const legsValue = legs.reduce((sum, leg) => {
    const tau = legDaysRemaining(leg, evalDay) / 365
    const sigma = effectiveSigma(leg.iv, ivShift)
    const price = bsPrice(leg.type, S, leg.strike, tau, sigma, r, q)
    return sum + legSign(leg.action) * price * 100 * leg.contracts
  }, 0)
  const stockValue = stockPosition ? stockSign(stockPosition) * stockPosition.shares * S : 0
  return legsValue + stockValue
}

/** Position-level Delta, in share-equivalents (i.e. already scaled by 100 * contracts per leg), including the stock position's delta if present. */
export function positionDelta(
  legs: OptionLeg[],
  S: number,
  evalDay: number,
  r: number,
  ivShift = 0,
  q = 0,
  stockPosition?: StockPosition
): number {
  const legsDelta = legs.reduce((sum, leg) => {
    const tau = legDaysRemaining(leg, evalDay) / 365
    const sigma = effectiveSigma(leg.iv, ivShift)
    const delta = bsDelta(leg.type, S, leg.strike, tau, sigma, r, q)
    return sum + legSign(leg.action) * delta * 100 * leg.contracts
  }, 0)
  const stockDelta = stockPosition ? stockSign(stockPosition) * stockPosition.shares : 0
  return legsDelta + stockDelta
}

/** Position-level Gamma, in share-equivalents. Stock has zero gamma, so no stock position parameter. */
export function positionGamma(legs: OptionLeg[], S: number, evalDay: number, r: number, ivShift = 0, q = 0): number {
  return legs.reduce((sum, leg) => {
    const tau = legDaysRemaining(leg, evalDay) / 365
    const sigma = effectiveSigma(leg.iv, ivShift)
    const gamma = bsGamma(S, leg.strike, tau, sigma, r, q)
    return sum + legSign(leg.action) * gamma * 100 * leg.contracts
  }, 0)
}

/**
 * Position-level Theta, in TOTAL dollars PER DAY (i.e. already scaled by
 * 100 * contracts per leg, and converted from `bsTheta`'s per-year rate by
 * dividing by 365). Stock has zero theta, so no stock position parameter.
 */
export function positionTheta(legs: OptionLeg[], S: number, evalDay: number, r: number, ivShift = 0, q = 0): number {
  return legs.reduce((sum, leg) => {
    const tau = legDaysRemaining(leg, evalDay) / 365
    const sigma = effectiveSigma(leg.iv, ivShift)
    const theta = bsTheta(leg.type, S, leg.strike, tau, sigma, r, q)
    return sum + (legSign(leg.action) * theta * 100 * leg.contracts) / 365
  }, 0)
}

export interface PricePnlRow {
  price: number
  /** Mark-to-market P&L at the selected evaluation day (T+n), via Black-Scholes. */
  plNow: number
  /** P&L at expiry (the existing piecewise-linear payoff). */
  plExpiry: number
  delta: number
  gamma: number
}

/**
 * Builds one row per price in `spotGrid` with both P&L views (T+n and
 * expiry) and the position Greeks at that price/day. Used for the table
 * view — the grid itself is generated by the caller (UI) at round-number
 * steps centred on the current underlying price.
 */
export function pricePnlTable(
  legs: OptionLeg[],
  spotGrid: number[],
  evalDay: number,
  r: number,
  ivShift = 0,
  q = 0,
  stockPosition?: StockPosition
): PricePnlRow[] {
  return spotGrid.map((price) => ({
    price,
    plNow: plAtTime(legs, price, evalDay, r, ivShift, q, stockPosition),
    plExpiry: totalPayoff(legs, price, stockPosition),
    delta: positionDelta(legs, price, evalDay, r, ivShift, q, stockPosition),
    gamma: positionGamma(legs, price, evalDay, r, ivShift, q),
  }))
}

// ─── P&L matrix (price rows × evaluation-date columns) ─────────────────────

export interface PnlMatrixCell {
  /** Mark-to-market P&L, in total dollars. */
  pl: number
  /** Mark-to-market position VALUE, in total dollars — feeds the "Value" output mode. */
  value: number
}

export interface PnlMatrixRow {
  price: number
  /** One cell per `dayOffsets` entry, in the same order. */
  cells: PnlMatrixCell[]
}

/**
 * Builds the evaluation-day column offsets (days from today) for the P&L
 * matrix, targeting up to 12 columns: short windows (<= 12 days to the
 * earliest expiry) show every day for a granular view; longer windows are
 * thinned to 12 evenly-spaced columns so the table doesn't get unreasonably
 * wide. The first offset is always 0 (today) and the last is always exactly
 * `earliestExpiryDays`, so the final matrix column lines up exactly with the
 * earliest leg's expiry payoff.
 */
export function buildDayOffsets(earliestExpiryDays: number): number[] {
  if (earliestExpiryDays <= 0) return [0]
  if (earliestExpiryDays <= 12) return Array.from({ length: earliestExpiryDays + 1 }, (_, i) => i)

  const columns = 12
  const offsets = new Set<number>([0])
  for (let i = 1; i < columns; i++) {
    offsets.add(Math.round((earliestExpiryDays * i) / (columns - 1)))
  }
  offsets.add(earliestExpiryDays)
  return Array.from(offsets).sort((a, b) => a - b)
}

/**
 * Builds the full P&L matrix: one row per price in `priceGrid`, one column
 * per entry in `dayOffsets`. Each cell carries both mark-to-market P&L and
 * total position VALUE at that price/evaluation day, via `plAtTime` /
 * `positionValue` (each leg priced with its own expiry/IV, shifted by
 * `ivShift`).
 */
export function pnlMatrix(
  legs: OptionLeg[],
  priceGrid: number[],
  dayOffsets: number[],
  r: number,
  ivShift = 0,
  q = 0,
  stockPosition?: StockPosition
): PnlMatrixRow[] {
  return priceGrid.map((price) => ({
    price,
    cells: dayOffsets.map((evalDay) => ({
      pl: plAtTime(legs, price, evalDay, r, ivShift, q, stockPosition),
      value: positionValue(legs, price, evalDay, r, ivShift, q, stockPosition),
    })),
  }))
}

// ─── Matrix output-mode transform ───────────────────────────────────────────

export type CellMode = 'pl' | 'pctRisk' | 'pctCost' | 'value'

const ZERO_EPSILON = 0.01

/**
 * Transforms a raw mark-to-market P&L cell into the selected output mode:
 *   - 'pl'      — the raw dollar P&L, unchanged.
 *   - 'pctRisk' — P&L as a percentage of max risk (|maxLoss|); falls back to
 *                 'pl' when maxRisk is unbounded ('Unlimited') or ~zero.
 *   - 'pctCost' — P&L as a percentage of entry cost; falls back to 'pl' when
 *                 entryCost is ~zero (e.g. a even-money position).
 *   - 'value'   — the total position market value, passed straight through.
 */
export function transformCell(
  plDollars: number,
  mode: CellMode,
  entryCost: number,
  maxRisk: number | 'Unlimited',
  positionValue: number
): number {
  switch (mode) {
    case 'pl':
      return plDollars
    case 'value':
      return positionValue
    case 'pctRisk':
      if (maxRisk === 'Unlimited' || Math.abs(maxRisk) < ZERO_EPSILON) return plDollars
      return (plDollars / Math.abs(maxRisk)) * 100
    case 'pctCost':
      if (Math.abs(entryCost) < ZERO_EPSILON) return plDollars
      return (plDollars / Math.abs(entryCost)) * 100
  }
}

/**
 * True when `transformCell` will actually apply a percentage transform for
 * this mode/inputs, vs. silently falling back to raw dollars (unbounded
 * maxRisk or ~zero entryCost). Lets the UI decide whether to render a "%"
 * suffix without duplicating `transformCell`'s fallback logic.
 */
export function isPercentMode(mode: CellMode, entryCost: number, maxRisk: number | 'Unlimited'): boolean {
  if (mode === 'pctRisk') return !(maxRisk === 'Unlimited' || Math.abs(maxRisk) < ZERO_EPSILON)
  if (mode === 'pctCost') return !(Math.abs(entryCost) < ZERO_EPSILON)
  return false
}

// ─── Summary metrics ────────────────────────────────────────────────────────

/** Options-only net premium in TOTAL dollars. Positive = debit (paid), negative = credit (received). */
export function netPremium(legs: OptionLeg[]): number {
  return legs.reduce((sum, leg) => {
    const signed = leg.action === 'buy' ? leg.premium : -leg.premium
    return sum + signed * 100 * leg.contracts
  }, 0)
}

/** Total cost to open the position — options net premium plus the stock position's cost basis, if any. Positive = net debit; negative = net credit. */
export function entryCost(legs: OptionLeg[], stockPosition?: StockPosition): number {
  const stockCost = stockPosition ? stockSign(stockPosition) * stockPosition.shares * stockPosition.entryPrice : 0
  return Math.round((netPremium(legs) + stockCost) * 100) / 100
}

/**
 * Slope of the combined payoff function (in total $ per $1 move in the
 * underlying) as price -> +Infinity. Only long/short calls and the stock
 * position contribute a non-zero slope at the far right of the domain —
 * puts flatten out to a constant. A covered call (long stock + short call
 * with matching share/contract size) nets to a zero slope, i.e. a capped
 * (finite) max profit.
 */
function rightTailSlope(legs: OptionLeg[], stockPosition?: StockPosition): number {
  const optionsSlope = legs.reduce((sum, leg) => {
    if (leg.type !== 'call') return sum
    const sign = leg.action === 'buy' ? 1 : -1
    return sum + sign * 100 * leg.contracts
  }, 0)
  const stockSlope = stockPosition ? stockSign(stockPosition) * stockPosition.shares : 0
  return optionsSlope + stockSlope
}

/** Sorted, deduplicated breakpoints of the piecewise-linear payoff function, plus 0. (A stock position adds a constant slope everywhere, not a new kink, so it doesn't add breakpoints.) */
function breakpoints(legs: OptionLeg[]): number[] {
  const strikes = new Set<number>([0, ...legs.map((l) => l.strike)])
  return Array.from(strikes).sort((a, b) => a - b)
}

/**
 * Computes net debit/credit, max profit, max loss, breakeven price(s), and
 * return-on-risk for the position (option legs, each valued at its own
 * expiry, plus an optional stock position).
 *
 * The combined payoff of vanilla long/short calls & puts plus a linear stock
 * position is piecewise-linear in the underlying price, with kinks only at
 * each leg's strike (the stock position only changes the SLOPE of every
 * segment — it doesn't add new kinks). So extrema occur either at a strike,
 * at the price floor (S = 0, since the underlying can't go negative), or —
 * if the payoff is still sloping at the far right of the domain — at
 * +Infinity ("Unlimited").
 */
export function computeSpreadSummary(legs: OptionLeg[], stockPosition?: StockPosition): SpreadSummary {
  if (legs.length === 0 && !stockPosition) {
    return { netPremium: 0, entryCost: 0, maxProfit: 0, maxLoss: 0, breakevens: [], returnOnRisk: null }
  }

  const points = breakpoints(legs)
  const payoffs = points.map((price) => totalPayoff(legs, price, stockPosition))
  const slope = rightTailSlope(legs, stockPosition)

  const maxProfit: number | 'Unlimited' = slope > 0 ? 'Unlimited' : Math.max(...payoffs)
  const maxLoss: number | 'Unlimited' = slope < 0 ? 'Unlimited' : Math.min(...payoffs)

  // ── Breakevens: sign changes across each finite segment, plus the open
  // right-hand ray beyond the last breakpoint when the payoff is still sloping.
  const breakevens: number[] = []
  for (let i = 0; i < points.length - 1; i++) {
    const [a, b] = [points[i], points[i + 1]]
    const [pa, pb] = [payoffs[i], payoffs[i + 1]]
    if (pa === 0) {
      breakevens.push(a)
      continue
    }
    if (pa < 0 !== pb < 0 && pb !== 0) {
      // Linear interpolation between (a, pa) and (b, pb) for the zero crossing.
      const price = a + (0 - pa) / (pb - pa) * (b - a)
      breakevens.push(price)
    }
  }
  // Last breakpoint itself, if it lands exactly on zero.
  const lastPayoff = payoffs[payoffs.length - 1]
  if (lastPayoff === 0) breakevens.push(points[points.length - 1])

  // Open right-hand ray: payoff(S) = lastPayoff + slope * (S - lastStrike) for S > lastStrike.
  if (slope !== 0) {
    const lastStrike = points[points.length - 1]
    const crossing = lastStrike - lastPayoff / slope
    if (crossing > lastStrike) breakevens.push(crossing)
  }

  const uniqueSortedBreakevens = Array.from(new Set(breakevens.map((b) => Math.round(b * 100) / 100))).sort(
    (a, b) => a - b
  )

  const roundedMaxProfit: number | 'Unlimited' = typeof maxProfit === 'number' ? Math.round(maxProfit * 100) / 100 : maxProfit
  const roundedMaxLoss: number | 'Unlimited' = typeof maxLoss === 'number' ? Math.round(maxLoss * 100) / 100 : maxLoss
  const returnOnRisk =
    typeof roundedMaxProfit === 'number' && typeof roundedMaxLoss === 'number' && roundedMaxLoss !== 0
      ? Math.round((roundedMaxProfit / Math.abs(roundedMaxLoss)) * 10000) / 10000
      : null

  return {
    netPremium: netPremium(legs),
    entryCost: entryCost(legs, stockPosition),
    maxProfit: roundedMaxProfit,
    maxLoss: roundedMaxLoss,
    breakevens: uniqueSortedBreakevens,
    returnOnRisk,
  }
}

// ─── Taylor / Greeks approximation ──────────────────────────────────────────

/**
 * Second-order Taylor (quadratic) approximation of position P&L, built
 * directly from Delta/Gamma/Theta rather than a full Black-Scholes
 * revaluation:
 *
 *   P&L ≈ Δ·(S − S0) + ½·Γ·(S − S0)² + θ·daysElapsed
 *
 * `deltaPos`/`gammaPos` are position-level Greeks in the same
 * share-equivalent units `positionDelta`/`positionGamma` return; `thetaPerDay`
 * is total dollars/day as `positionTheta` returns. `S0` is the anchor price
 * the Greeks were measured at (usually the current underlying price); `S` is
 * the price to evaluate at. Exact at S = S0, daysElapsed = 0, and diverges
 * from the true revaluation curve away from that point — that divergence is
 * the whole point of overlaying this on the exact chart.
 */
export function taylorPnl(
  deltaPos: number,
  gammaPos: number,
  thetaPerDay: number,
  S0: number,
  S: number,
  daysElapsed: number
): number {
  const dS = S - S0
  return deltaPos * dS + 0.5 * gammaPos * dS * dS + thetaPerDay * daysElapsed
}

// ─── Strategy templates ─────────────────────────────────────────────────────

let legIdCounter = 0
function nextLegId(): string {
  legIdCounter += 1
  return `leg-${legIdCounter}-${Date.now().toString(36)}`
}

/** Rounds a price to the nearest multiple of `step` (default $5 strikes). */
function roundToStep(value: number, step = 5): number {
  return Math.max(step, Math.round(value / step) * step)
}

/** Rough placeholder premium so the legs editor isn't blank — always user-editable. */
function estimatePremium(underlying: number, strike: number, type: OptionType): number {
  const intrinsic = type === 'call' ? Math.max(underlying - strike, 0) : Math.max(strike - underlying, 0)
  const timeValue = Math.max(1, underlying * 0.02)
  return Math.round((intrinsic + timeValue) * 100) / 100
}

const DEFAULT_TEMPLATE_EXPIRY_DAYS = 30
const DEFAULT_TEMPLATE_IV = 0.25

/**
 * Builds the two starter legs for a template, with strikes bracketing the
 * given underlying price. Every leg defaults to `expiryDays` (today + 30 by
 * default) and `iv` (the current strategy-wide default). Returns an empty
 * array for 'custom' — the caller adds legs manually.
 */
export function buildTemplateLegs(
  template: SpreadTemplate,
  underlyingPrice: number,
  expiryDays = DEFAULT_TEMPLATE_EXPIRY_DAYS,
  iv = DEFAULT_TEMPLATE_IV
): OptionLeg[] {
  const lower = roundToStep(underlyingPrice * 0.95)
  const upper = roundToStep(underlyingPrice * 1.05) === lower ? lower + 5 : roundToStep(underlyingPrice * 1.05)

  const leg = (action: OptionAction, type: OptionType, strike: number): OptionLeg => ({
    id: nextLegId(),
    action,
    type,
    strike,
    premium: estimatePremium(underlyingPrice, strike, type),
    contracts: 1,
    expiryDays,
    iv,
  })

  switch (template) {
    case 'bull_call_spread':
      return [leg('buy', 'call', lower), leg('sell', 'call', upper)]
    case 'bear_call_spread':
      return [leg('sell', 'call', lower), leg('buy', 'call', upper)]
    case 'bull_put_spread':
      return [leg('sell', 'put', upper), leg('buy', 'put', lower)]
    case 'bear_put_spread':
      return [leg('buy', 'put', upper), leg('sell', 'put', lower)]
    case 'custom':
    default:
      return []
  }
}

export function createEmptyLeg(
  underlyingPrice: number,
  expiryDays = DEFAULT_TEMPLATE_EXPIRY_DAYS,
  iv = DEFAULT_TEMPLATE_IV
): OptionLeg {
  const strike = roundToStep(underlyingPrice)
  return {
    id: nextLegId(),
    action: 'buy',
    type: 'call',
    strike,
    premium: estimatePremium(underlyingPrice, strike, 'call'),
    contracts: 1,
    expiryDays,
    iv,
  }
}
